import { viewStateKey } from "@baton/core";
import { useParams, useSearchParams } from "react-router";
import { useRuntime } from "../app/RuntimeProvider.tsx";
import { projectScope } from "../app/viewScopes.ts";
import { chosenMeetingCalendarView, NARROW_QUERY, skeletonOf } from "../meetings/calendar/views.ts";
import { chosenMeetingLayout } from "../meetings/list/choice.ts";
import { useMediaQuery } from "../shell/layout/AppShell.tsx";
import { AccountsPageSkeleton, BoardPageSkeleton, BoardSkeleton, MeetingsCalendarPageSkeleton, ProjectItemsSkeleton } from "../skeletons/Skeletons.tsx";

/**
 * FR-015-090, `contracts/screens.md` § States of every screen: the project items route's loading
 * state is the skeleton of the layout that will mount, the URL's `layout` or the one remembered
 * for the project, so a board never paints a list skeleton on its way in.
 */
export function ProjectItemsFallback() {
  const runtime = useRuntime();
  const [search] = useSearchParams();
  const { ws = "", project = "" } = useParams();
  const layout = search.get("layout") ?? runtime.storage.get(viewStateKey, projectScope(ws, project))?.layout;
  return layout === "board" ? <BoardSkeleton /> : <ProjectItemsSkeleton />;
}

/** The client list's loading state (2026-09-27): the board's skeleton when the URL or the team's remembered choice opens the board, else the list's. */
export function AccountsFallback() {
  const runtime = useRuntime();
  const [search] = useSearchParams();
  const { ws = "" } = useParams();
  const layout = search.get("layout") ?? runtime.storage.get(viewStateKey, `clients:${ws}`)?.layout;
  return layout === "board" ? <BoardPageSkeleton controls={5} /> : <AccountsPageSkeleton columns={5} controls={5} />;
}

/**
 * The Meetings page's loading state (018 FR-018-282 as amended 2026-09-30): the Calendar layout's
 * skeleton, of the view that will mount, unless the URL or the Team's remembered choice opens the List.
 */
export function MeetingsFallback() {
  const runtime = useRuntime();
  const [search] = useSearchParams();
  const { ws = "" } = useParams();
  const narrow = useMediaQuery(NARROW_QUERY);
  if (chosenMeetingLayout(runtime, ws, search) === "list") return <AccountsPageSkeleton columns={6} controls={1} />;
  return <MeetingsCalendarPageSkeleton view={skeletonOf(chosenMeetingCalendarView(runtime, ws, search, narrow))} />;
}
